import React,{useState,useEffect}from"react";
import{CheckCircle2,Circle,XCircle,Clock,Lock,X,GitBranch,Link2,ThumbsUp,ThumbsDown,ExternalLink,AlertCircle,Users,User,FileText,MessageSquare,Mail,Edit2,Trash2,Save,Tag,Calendar,Eye,UserCheck,Radio,Layers,CornerUpLeft,Repeat,History,ChevronDown,ChevronUp,ChevronsUpDown,Upload,Paperclip}from"lucide-react";
import{BLUE,VIS_META,DOC_META,fmtDate,isOverdue,isLocked,needsStandupUpdate,dispName,getAssignableUsers,nextRecurDate}from"../config";
import{StaBadge,PriBadge,AccBadge,TeamsBadge,VisIcon}from"./UI";
import DepsPicker from"./DepsPicker";
import AssigneePicker,{AssigneeChip}from"./AssigneePicker";
import * as Graph from"../Graph";

export function TaskRow({task,all,selected,onSelect,onStandupToggle,cu,canStandup,projects=[]}){
  const od=isOverdue(task);
  const locked=isLocked(task,all);
  const stale=needsStandupUpdate(task);
  const vm=VIS_META[task.vis]||VIS_META.personal;
  const handleStandup=(e)=>{e.stopPropagation();if(onStandupToggle)onStandupToggle(task,!task.standup);};
  const cuEmail=cu?.email?.toLowerCase()||"";
  const isMyTask=task.own?.toLowerCase()===cuEmail||(task.asgn?.toLowerCase()===cuEmail&&task.acc==="accepted");
  const showStandup=canStandup||isMyTask;
  return(
    <div onClick={onSelect} className={"bg-white border rounded-xl px-4 py-3.5 cursor-pointer hover:shadow-sm transition-shadow "+(stale&&task.standup?"border-amber-300 bg-amber-50":od||locked?"border-red-200":task.acc==="pending"?"border-amber-300":"border-slate-200")} style={selected?{borderColor:BLUE,boxShadow:"0 0 0 3px "+BLUE+"22"}:{}}>
      <div className="flex items-center gap-3">
        <div className="shrink-0">
          {task.sta==="completed"?<CheckCircle2 size={18} className="text-emerald-500"/>:task.sta==="review"?<CheckCircle2 size={18} className="text-purple-400"/>:locked?<Lock size={18} className="text-red-400"/>:task.sta==="blocked"?<XCircle size={18} className="text-red-400"/>:task.sta==="pending"?<Clock size={18} className="text-amber-400"/>:task.sta==="declined"?<XCircle size={18} className="text-red-400"/>:<Circle size={18} className="text-slate-300"/>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <p className={"text-sm font-semibold "+(task.sta==="completed"?"line-through text-slate-400":task.sta==="declined"?"text-slate-400":"")}>{task.title}</p>
            {task.standup&&<span className={"text-xs px-1.5 py-0.5 rounded font-semibold flex items-center gap-1 "+(stale?"bg-amber-200 text-amber-800":"bg-blue-100 text-blue-700")}><Radio size={9}/>Standup{stale?" ⚠":""}</span>}
            {task.recurrence?.enabled&&<span className="text-xs px-1.5 py-0.5 rounded font-semibold flex items-center gap-1 bg-indigo-100 text-indigo-700"><Repeat size={9}/>Recurring</span>}
            {task.src==="teams"&&<TeamsBadge/>}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-slate-400 flex items-center gap-1"><VisIcon type={task.vis}/><span>{vm.label}</span></span>
            <StaBadge sta={locked?"blocked":task.sta}/>
            <PriBadge pri={task.pri}/>
            {od&&<span className="text-xs text-red-500 font-semibold">Overdue</span>}
            <span className="text-xs text-slate-400">{fmtDate(task.dueDate)}</span>
            {task.own&&task.own!==cu.email&&<span className="text-xs text-slate-400 flex items-center gap-1"><UserCheck size={9}/>by {dispName(task.own)}</span>}
            {task.asgn&&task.asgn!==task.own&&(
              task.acc==="accepted"
                ?<span className="text-xs font-semibold px-1.5 py-0.5 rounded-full flex items-center gap-1 bg-emerald-50 text-emerald-700"><UserCheck size={9}/>→ {dispName(task.asgn)}</span>
                :task.acc==="declined"
                  ?<span className="text-xs font-semibold px-1.5 py-0.5 rounded-full flex items-center gap-1 bg-red-50 text-red-600"><User size={9}/>→ {dispName(task.asgn)} (declined)</span>
                  :task.acc==="pending"
                    ?<span className="text-xs font-semibold px-1.5 py-0.5 rounded-full flex items-center gap-1 bg-amber-50 text-amber-700"><User size={9}/>→ {dispName(task.asgn)} (pending)</span>
                    :<span className="text-xs text-slate-400 flex items-center gap-1"><User size={9}/>{dispName(task.asgn)}</span>
            )}
            {task.project&&projects.length>0&&(()=>{const p=projects.find(x=>x.spId===task.project);return p?<span className="text-xs flex items-center gap-1"><Layers size={9} className="text-indigo-400 shrink-0"/>{p.customerName&&<span className="text-slate-400">{p.customerName} ·</span>}<span className="text-indigo-600 font-medium">{p.jobNumber?p.jobNumber+" – "+p.projectName:p.projectName}</span></span>:null;})()}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-2">
          {task.docs?.length>0&&<span className="text-xs text-slate-400 flex items-center gap-1"><Link2 size={11}/>{task.docs.length}</span>}
          {showStandup&&(
            <button onClick={handleStandup} title={task.standup?"Remove from Daily Standup":"Add to Daily Standup"} className={"p-1 rounded-lg transition-colors "+(task.standup?"text-blue-600 bg-blue-100 hover:bg-blue-200":"text-slate-300 hover:text-blue-400 hover:bg-blue-50")}>
              <Radio size={14}/>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailRow({icon,label,children}){
  return(
    <div className="flex items-start gap-3 py-2.5 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-1.5 w-28 shrink-0 text-xs text-slate-400 font-medium pt-0.5">{icon}{label}</div>
      <div className="flex-1 text-sm text-slate-700">{children}</div>
    </div>
  );
}

export function Detail({task,all,onClose,onAccept,onDecline,onApprove,onReopen,onEdit,onDelete,onStandupToggle,onRequestUpdate,onSendBack,onCompleteOccurrence,cu,allUsers,projects=[],canStandup}){
  const deps=task.deps.map(id=>all.find(x=>x.id===id||x.spId===id)).filter(Boolean);
  const[editing,setEditing]=useState(false);
  const[editForm,setEditForm]=useState({});
  const[saving,setSaving]=useState(false);
  const[showHistory,setShowHistory]=useState(false);
  const[showActivity,setShowActivity]=useState(false);
  const[activity,setActivity]=useState([]);
  const[actLoading,setActLoading]=useState(false);
  const[showDepsPicker,setShowDepsPicker]=useState(false);
  const[showAssigneePicker,setShowAssigneePicker]=useState(false);
  const[showReassignPicker,setShowReassignPicker]=useState(false);
  const[inlineNote,setInlineNote]=useState(null); // null=view, string=editing
  const[recurDone,setRecurDone]=useState(false);
  const[noteSaving,setNoteSaving]=useState(false);
  const[attachUploading,setAttachUploading]=useState(false);
  const[attachError,setAttachError]=useState("");
  const[dragOver,setDragOver]=useState(false);
  const[updateStatus,setUpdateStatus]=useState(null); // null | 'sending' | 'sent' | 'error'
  const[showUpdateModal,setShowUpdateModal]=useState(false);
  const[updateNote,setUpdateNote]=useState("");
  const saveInlineNote=async()=>{
    if(inlineNote===null||inlineNote===(task.notes||""))return setInlineNote(null);
    setNoteSaving(true);
    const now=new Date();
    const updated={...task,notes:inlineNote};
    // Record structured note history entry for reporting
    const nh=[...(task.noteHistory||[])];
    nh.push({date:now.toISOString(),by:cu.email,text:inlineNote});
    updated.noteHistory=nh;
    // If standup is enabled, also record a standup history entry so "Needs Update" clears
    if(task.standup){
      const today=now.toISOString().slice(0,10);
      const history=[...(task.standupHistory||[])];
      const todayIdx=history.findIndex(h=>h.date===today);
      const entry={date:today,status:task.sta,note:inlineNote,updatedAt:now.toISOString()};
      if(todayIdx>=0) history[todayIdx]=entry;
      else history.push(entry);
      updated.standupHistory=history;
    }
    await onEdit(updated);
    setNoteSaving(false);setInlineNote(null);
  };
  const pasteFromClipboard=async()=>{
    try{
      const items=await navigator.clipboard.read();
      for(const item of items){
        const imgType=item.types.find(t=>t.startsWith("image/"));
        if(imgType){
          const blob=await item.getType(imgType);
          const ext=imgType.split("/")[1]||"png";
          const file=new File([blob],`clipboard-${Date.now()}.${ext}`,{type:imgType});
          await uploadAttachment(file);
          return;
        }
      }
      setAttachError("No image found on clipboard. Copy an image first.");
    }catch(e){
      // User denied permission or API not available — fall back to focus+paste instruction
      setAttachError("Click inside the drop zone below, then press Ctrl+V.");
    }
  };
  const uploadAttachment=async(file)=>{
    if(!file)return;
    const docs=task.docs||[];
    if(docs.length>=3){setAttachError("Max 3 attachments per task.");return;}
    const MB=file.size/1024/1024;
    if(MB>25){setAttachError("File must be under 25 MB.");return;}
    setAttachUploading(true);setAttachError("");
    try{
      const doc=await Graph.uploadTaskAttachment(file,task.spId||task.id);
      const newDocs=[...docs,doc];
      await onEdit({...task,docs:newDocs});
    }catch(e){setAttachError("Upload failed: "+e.message);}
    finally{setAttachUploading(false);}
  };

  const removeAttachment=async(idx)=>{
    const newDocs=(task.docs||[]).filter((_,i)=>i!==idx);
    await onEdit({...task,docs:newDocs});
  };

  useEffect(()=>{setInlineNote(null);setAttachError("");setDragOver(false);setRecurDone(false);},[task.id]);
  useEffect(()=>{
    if(!showActivity||!task.spId)return;
    setActLoading(true);
    Graph.getTaskHistory(task.id).then(setActivity).catch(()=>setActivity([])).finally(()=>setActLoading(false));
  },[showActivity,task.spId]);
  useEffect(()=>{
    const onPaste=e=>{
      if(editing)return; // don't intercept when editing form
      const items=Array.from(e.clipboardData?.items||[]);
      const fileItem=items.find(i=>i.kind==="file");
      if(fileItem){e.preventDefault();uploadAttachment(fileItem.getAsFile());}
    };
    window.addEventListener("paste",onPaste);
    return()=>window.removeEventListener("paste",onPaste);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[task.id,task.docs,editing]);
  const DAYS=["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const FREQ_OPTS=[{v:"daily",lb:"Every day"},{v:"weekdays",lb:"Every weekday (Mon–Fri)"},{v:"weekly",lb:"Weekly"},{v:"biweekly",lb:"Every 2 weeks"},{v:"monthly",lb:"Monthly"},{v:"custom",lb:"Custom — every N days"}];
  const startEdit=()=>{
    const r=task.recurrence||{};
    // Force vis="company" for company list tasks — prevents accidental list migration (delete+recreate)
    const vis=task.listType==="company"?"company":task.vis;
    setEditForm({title:task.title,desc:task.desc||"",pri:task.pri,sta:task.sta,dueDate:task.dueDate||"",notes:task.notes||"",vis,asgn:task.asgn||"",project:task.project||"",deps:task.deps||[],recur:r.enabled||false,recurFreq:r.freq||"weekly",recurDays:r.days||[],recurInterval:r.interval||7,recurDay:r.dayOfMonth||1});
    setEditing(true);
  };
  const cancelEdit=()=>setEditing(false);
  const efUpd=(k,v)=>setEditForm(p=>({...p,[k]:v}));
  const togDay=d=>efUpd("recurDays",editForm.recurDays.includes(d)?editForm.recurDays.filter(x=>x!==d):[...editForm.recurDays,d]);
  const saveEdit=async()=>{
    setSaving(true);
    const recurrence=editForm.recur?{enabled:true,freq:editForm.recurFreq,days:editForm.recurDays,interval:editForm.recurInterval,dayOfMonth:editForm.recurDay}:null;
    let toSave={...task,...editForm,deps:editForm.deps||[],recurrence};
    const isAssignable=editForm.vis==="assigned"||(editForm.vis==="company"||task.listType==="company");
    if(isAssignable&&editForm.asgn!=null&&editForm.asgn.toLowerCase()!==(task.asgn||"").toLowerCase()){
      if(editForm.asgn&&editForm.asgn.toLowerCase()!==cu.email.toLowerCase()){
        toSave={...toSave,acc:"pending",reqBy:cu.email};
      } else {
        toSave={...toSave,acc:"",reqBy:""};
      }
    }
    // Record note history if notes changed
    if((editForm.notes||"")!==(task.notes||"")){
      const nh=[...(task.noteHistory||[])];
      nh.push({date:new Date().toISOString(),by:cu.email,text:editForm.notes||""});
      toSave.noteHistory=nh;
    }
    await onEdit(toSave);
    setEditing(false);setSaving(false);
  };
  const handleDelete=async()=>{if(!window.confirm("Delete this task? This cannot be undone."))return;await onDelete(task.id);onClose();};
  const locked=isLocked(task,all);
  const od=isOverdue(task);
  const stale=needsStandupUpdate(task);
  const pendingMe=task.acc==="pending"&&task.asgn.toLowerCase()===cu.email.toLowerCase();
  const isOwner=task.own.toLowerCase()===cu.email.toLowerCase();
  const isAdmin=cu.role==="Admin";
  const isManagerRole=cu.role==="Manager"||cu.role==="Admin";
  const ownerRecord=(allUsers||[]).find(u=>u.email.toLowerCase()===task.own.toLowerCase());
  const isManagerOfOwner=isManagerRole&&ownerRecord&&(ownerRecord.manager1?.toLowerCase()===cu.email.toLowerCase()||ownerRecord.manager2?.toLowerCase()===cu.email.toLowerCase());
  const assigneeRecord=(allUsers||[]).find(u=>u.email.toLowerCase()===task.asgn?.toLowerCase());
  const isManagerOfAssignee=isManagerRole&&assigneeRecord&&(assigneeRecord.manager1?.toLowerCase()===cu.email.toLowerCase()||assigneeRecord.manager2?.toLowerCase()===cu.email.toLowerCase());
  const isAcceptedAssignee=task.asgn&&task.asgn.toLowerCase()===cu.email.toLowerCase()&&task.acc==="accepted";
  const isMyTask=isOwner||isAcceptedAssignee;
  const showStandup=canStandup||isMyTask;
  const canEdit=isOwner||isAdmin||isManagerOfOwner||isAcceptedAssignee;
  const isReqBy=!!(task.reqBy&&task.reqBy.toLowerCase()===cu.email.toLowerCase());
  const canReassign=isAdmin||isManagerOfOwner||isOwner||(isManagerRole&&isAcceptedAssignee)||isReqBy;
  const canApprove=isOwner||isAdmin||isManagerOfOwner||isManagerOfAssignee||(isReqBy&&isManagerRole);
  const assignable=getAssignableUsers(cu,allUsers||[]);
  const vm=VIS_META[task.vis]||VIS_META.personal;
  const sortedHistory=[...(task.standupHistory||[])].sort((a,b)=>b.date.localeCompare(a.date));

  return(
    <><div className="relative flex flex-col h-full">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 shrink-0 bg-slate-50">
        <div className="flex flex-wrap gap-1.5">
          <StaBadge sta={locked?"blocked":task.sta}/>
          {task.acc&&<AccBadge acc={task.acc}/>}
          {od&&<span className="text-xs text-red-500 font-semibold flex items-center gap-1"><AlertCircle size={10}/>Overdue</span>}
          {locked&&<span className="text-xs text-red-500 font-semibold flex items-center gap-1"><Lock size={10}/>Locked</span>}
          {task.standup&&<span className={"text-xs px-2 py-0.5 rounded-full font-semibold flex items-center gap-1 "+(stale?"bg-amber-200 text-amber-800 border border-amber-300":"bg-blue-100 text-blue-700 border border-blue-200")}><Radio size={9}/>Standup{stale?" — Needs Update":""}</span>}
          {task.vis==="personal"&&<span className="text-xs bg-slate-100 text-slate-500 border border-slate-200 px-2 py-0.5 rounded-full flex items-center gap-1"><Lock size={9}/>Private</span>}
          {task.vis==="company"&&<span className="text-xs border px-2 py-0.5 rounded-full flex items-center gap-1" style={{background:"#EEF4FF",color:BLUE,borderColor:"#BFCFEA"}}><Users size={9}/>Company</span>}
          {task.src==="teams"&&<TeamsBadge/>}
        </div>
        <div className="flex items-center gap-1">
          {editing?(
            <>
              <button onClick={cancelEdit} className="text-xs px-2.5 py-1 border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600">Cancel</button>
              <button onClick={saveEdit} disabled={saving||(editForm.vis==="company"&&!editForm.project)} className="text-xs px-2.5 py-1 text-white rounded-lg font-semibold flex items-center gap-1 disabled:opacity-50" style={{background:"#1D5FA5"}}>{saving&&<span className="animate-spin inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full"/>}<Save size={11}/>Save</button>
            </>
          ):(
            <>
              {canEdit&&<button onClick={startEdit} className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-500" title="Edit task"><Edit2 size={14}/></button>}
              {(isAdmin||isManagerOfOwner)&&<button onClick={handleDelete} className="p-1.5 rounded-lg hover:bg-red-100 text-red-400" title="Delete task"><Trash2 size={14}/></button>}
              {onRequestUpdate&&(task.asgn?task.asgn.toLowerCase()!==cu.email.toLowerCase():task.own&&task.own.toLowerCase()!==cu.email.toLowerCase())&&task.sta!=="completed"&&task.sta!=="declined"&&(
                updateStatus==="sent"
                  ?<span className="text-xs px-2 py-1 rounded-lg border border-green-200 bg-green-50 text-green-700 flex items-center gap-1"><CheckCircle2 size={11}/>Sent!</span>
                  :updateStatus==="error"
                    ?<span className="text-xs px-2 py-1 rounded-lg border border-red-200 bg-red-50 text-red-600 flex items-center gap-1"><AlertCircle size={11}/>Failed</span>
                    :<button onClick={()=>{setUpdateNote("");setShowUpdateModal(true);}} className="text-xs px-2 py-1 rounded-lg border border-slate-200 hover:bg-amber-50 hover:border-amber-300 text-slate-500 hover:text-amber-700 flex items-center gap-1" title={task.asgn?"Request a status update from the assignee":"Request a status update from the owner"}><MessageSquare size={11}/>Request Update</button>
              )}
            </>
          )}
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-200"><X size={16}/></button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 text-sm">
        {editing?(
          <div className="space-y-3">
            <div><label className="block text-xs font-semibold text-slate-500 mb-1">Title</label><input autoComplete="off" value={editForm.title} onChange={e=>setEditForm(p=>({...p,title:e.target.value}))} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none font-semibold"/></div>
            <div><label className="block text-xs font-semibold text-slate-500 mb-1">Description</label><textarea autoComplete="off" value={editForm.desc} onChange={e=>setEditForm(p=>({...p,desc:e.target.value}))} rows={3} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none resize-none"/></div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="block text-xs font-semibold text-slate-500 mb-1">Priority</label>
                <select value={editForm.pri} onChange={e=>setEditForm(p=>({...p,pri:e.target.value}))} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
                </select></div>
              <div><label className="block text-xs font-semibold text-slate-500 mb-1">Status</label>
                <select value={editForm.sta} onChange={e=>setEditForm(p=>({...p,sta:e.target.value}))} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="todo">To Do</option><option value="in_progress">In Progress</option><option value="completed">Completed</option><option value="blocked">Blocked</option>{editForm.sta==="review"&&<option value="review">Needs Approval (system)</option>}
                </select></div>
              <div><label className="block text-xs font-semibold text-slate-500 mb-1">Due Date</label><input type="date" autoComplete="off" value={editForm.dueDate} onChange={e=>setEditForm(p=>({...p,dueDate:e.target.value}))} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none"/></div>
            </div>
            <div><label className="block text-xs font-semibold text-slate-500 mb-1">Notes</label><textarea autoComplete="off" value={editForm.notes} onChange={e=>setEditForm(p=>({...p,notes:e.target.value}))} rows={3} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none resize-none"/></div>

            {/* Recurring */}
            <div className="space-y-2">
              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <input type="checkbox" checked={editForm.recur||false} onChange={e=>efUpd("recur",e.target.checked)} className="accent-blue-600 w-4 h-4"/>
                <span className="text-xs font-semibold text-slate-500 flex items-center gap-1.5"><Repeat size={11}/>Recurring task</span>
              </label>
              {editForm.recur&&(
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">Frequency</label>
                    <select value={editForm.recurFreq||"weekly"} onChange={e=>efUpd("recurFreq",e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none">
                      {FREQ_OPTS.map(o=><option key={o.v} value={o.v}>{o.lb}</option>)}
                    </select>
                  </div>
                  {(editForm.recurFreq==="weekly"||editForm.recurFreq==="biweekly")&&(
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-2">On days</label>
                      <div className="flex gap-1.5 flex-wrap">
                        {DAYS.map(d=>(
                          <button key={d} type="button" onClick={()=>togDay(d)} className={"text-xs px-2.5 py-1 rounded-full font-medium border transition-colors "+(( editForm.recurDays||[]).includes(d)?"border-transparent text-white":"border-slate-200 text-slate-500 hover:bg-slate-100")} style={(editForm.recurDays||[]).includes(d)?{background:BLUE}:{}}>
                            {d}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {editForm.recurFreq==="monthly"&&(
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">Day of month</label>
                      <select value={editForm.recurDay||1} onChange={e=>efUpd("recurDay",Number(e.target.value))} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none">
                        {Array.from({length:31},(_,i)=>i+1).map(d=><option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                  )}
                  {editForm.recurFreq==="custom"&&(
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">Every N days</label>
                      <input type="number" min={1} max={365} autoComplete="off" value={editForm.recurInterval||7} onChange={e=>efUpd("recurInterval",Number(e.target.value))} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none"/>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Dependencies */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-slate-500 flex items-center gap-1"><GitBranch size={11}/>Dependencies</label>
                <button type="button" onClick={()=>setShowDepsPicker(true)} className="text-xs px-2.5 py-1 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 flex items-center gap-1">
                  <GitBranch size={11}/>Select{(editForm.deps||[]).length>0&&<span className="ml-1 bg-blue-100 text-blue-700 font-bold rounded-full px-1.5 text-xs">{(editForm.deps||[]).length}</span>}
                </button>
              </div>
              {(editForm.deps||[]).length>0?(
                <div className="space-y-1">
                  {(editForm.deps||[]).map(id=>{const t=all.find(x=>x.id===id);return t?(<div key={id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200"><span className="flex-1 text-xs truncate">{t.title}</span><StaBadge sta={t.sta}/><button type="button" onClick={()=>efUpd("deps",(editForm.deps||[]).filter(x=>x!==id))} className="text-slate-300 hover:text-red-400"><X size={11}/></button></div>):null;})}
                </div>
              ):<p className="text-xs text-slate-400 italic">None</p>}
            </div>

            {task.listType!=="company"&&(
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Visibility</label>
              <div className="grid grid-cols-3 gap-2">
                {[{v:"personal",lb:"Personal"},{v:"assigned",lb:"Assigned"},{v:"company",lb:"Company"}].map(({v,lb})=>(
                  <button key={v} type="button" onClick={()=>setEditForm(p=>({...p,vis:v}))} className={"border rounded-xl p-2.5 text-left "+(editForm.vis===v?"border-transparent":"border-slate-200 hover:bg-slate-50")} style={editForm.vis===v?{background:BLUE}:{}}>
                    <div className={"mb-1 "+(editForm.vis===v?"text-white":"text-slate-500")}><VisIcon type={v} size={14}/></div>
                    <p className={"text-xs font-bold "+(editForm.vis===v?"text-white":"text-slate-700")}>{lb}</p>
                  </button>
                ))}
              </div>
            </div>
            )}
            {(editForm.vis==="assigned"||editForm.vis==="company"||task.listType==="company")&&(
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Assign to{editForm.vis==="assigned"&&<span className="text-red-500"> *</span>}<span className="text-slate-400 font-normal ml-1">{editForm.vis==="company"||task.listType==="company"?" (optional)":""}</span></label>
                {canReassign
                  ?assignable.length===0
                    ?<p className="text-xs text-slate-400 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">No users available to assign.</p>
                    :<button type="button" onClick={()=>setShowAssigneePicker(true)} className="w-full flex items-center justify-between border border-slate-200 rounded-xl px-3 py-2.5 bg-white hover:bg-slate-50 transition-colors">
                      <AssigneeChip email={editForm.asgn} displayName={(allUsers||[]).find(u=>u.email.toLowerCase()===(editForm.asgn||"").toLowerCase())?.displayName}/>
                      <ChevronsUpDown size={13} className="text-slate-400 shrink-0 ml-2"/>
                    </button>
                  :<p className="text-sm text-slate-500 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg">{editForm.asgn?dispName(editForm.asgn):"—"}<span className="ml-2 text-xs text-slate-400">(only managers/admins can reassign)</span></p>
                }
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Project{editForm.vis==="company"&&<span className="text-red-500"> *</span>}</label>
              <select value={editForm.project||""} onChange={e=>setEditForm(p=>({...p,project:e.target.value}))} className={"w-full border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none "+(editForm.vis==="company"&&!editForm.project?"border-red-300":"border-slate-200")}>
                <option value="">-- No project --</option>
                {projects.filter(p=>p.isActive&&p.projectType==="customer").length>0&&<optgroup label="Customer Projects">{projects.filter(p=>p.isActive&&p.projectType==="customer").sort((a,b)=>a.customerName.localeCompare(b.customerName)).map(p=><option key={p.spId} value={p.spId}>{Graph.projectDisplayName(p)}{p.isQuote?" [Quote]":""}</option>)}</optgroup>}
                {projects.filter(p=>p.isActive&&p.projectType==="internal").length>0&&<optgroup label="Internal">{projects.filter(p=>p.isActive&&p.projectType==="internal").map(p=><option key={p.spId} value={p.spId}>{p.projectName}</option>)}</optgroup>}
              </select>
            </div>
          </div>
        ):(
          <>
            <h2 className={"text-base font-semibold leading-snug "+(task.sta==="completed"?"line-through text-slate-400":task.sta==="declined"?"text-slate-400":"")}>{task.title}</h2>
            {task.desc&&<div className="bg-slate-50 rounded-xl px-4 py-3 text-slate-600 leading-relaxed">{task.desc}</div>}

            {pendingMe&&(
              <div className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-3">
                <p className="text-sm font-semibold text-amber-800 mb-0.5">Task assigned to you</p>
                {task.reqBy&&<p className="text-xs text-amber-700 mb-2">From: {dispName(task.reqBy,allUsers)}</p>}
                <div className="flex gap-2">
                  <button onClick={()=>onDecline(task.id)} className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg border border-red-300 text-red-600 bg-white text-sm font-medium hover:bg-red-50"><ThumbsDown size={12}/>Decline</button>
                  <button onClick={()=>onAccept(task.id)} className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-white text-sm font-medium hover:opacity-90" style={{background:BLUE}}><ThumbsUp size={12}/>Accept</button>
                </div>
              </div>
            )}

            {/* Return-to-sender: assignee (manager) who has already accepted can return the task */}
            {!isOwner&&!pendingMe&&task.asgn?.toLowerCase()===cu.email.toLowerCase()&&task.acc==="accepted"&&task.reqBy&&(
              <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
                <p className="text-xs font-semibold text-slate-500 mb-1 flex items-center gap-1"><CornerUpLeft size={11}/>Return or Re-assign</p>
                <p className="text-xs text-slate-400 mb-2">You can return this task to <strong>{dispName(task.reqBy,allUsers)}</strong>, or re-assign it via Edit.</p>
                <button onClick={()=>onDecline(task.id)} className="text-xs px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-100 flex items-center gap-1.5"><CornerUpLeft size={11}/>Return to {dispName(task.reqBy,allUsers)}</button>
              </div>
            )}

            {isOwner&&task.acc&&!pendingMe&&task.sta!=="review"&&(
              <div className={"rounded-xl px-4 py-3 border "+(task.acc==="accepted"?"bg-emerald-50 border-emerald-200":task.acc==="declined"?"bg-red-50 border-red-200":"bg-amber-50 border-amber-200")}>
                <p className="text-xs font-semibold" style={{color:task.acc==="accepted"?"#059669":task.acc==="declined"?"#DC2626":"#92400E"}}>
                  {task.acc==="accepted"?"Accepted by "+dispName(task.asgn,allUsers):task.acc==="declined"?"Declined by "+dispName(task.asgn,allUsers):"Awaiting response from "+dispName(task.asgn,allUsers)}
                </p>
                {task.acc==="declined"&&(
                  <div className="flex gap-2 mt-2 flex-wrap">
                    <button onClick={()=>onSendBack&&onSendBack(task.id)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-lg" style={{background:"#DC2626"}} title="Re-send to the same person">↩ Send Back to {dispName(task.asgn,allUsers)}</button>
                    <button onClick={()=>setShowReassignPicker(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-100" title="Assign to someone else">⟳ Reassign to Someone Else</button>
                  </div>
                )}
              </div>
            )}

            {/* Recurring task — Done for today / Complete & Stop */}
            {task.recurrence?.enabled&&isOwner&&task.sta!=="completed"&&(
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3">
                <p className="text-xs font-semibold text-indigo-700 mb-1 flex items-center gap-1.5"><Repeat size={11}/>Recurring Task — {task.recurrence.freq==="daily"?"Daily":task.recurrence.freq==="weekdays"?"Every Weekday":task.recurrence.freq==="weekly"?"Weekly":task.recurrence.freq==="biweekly"?"Every 2 Weeks":task.recurrence.freq==="monthly"?"Monthly":`Every ${task.recurrence.interval||7} days`}</p>
                {recurDone?(
                  <p className="text-xs text-indigo-600 flex items-center gap-1"><CheckCircle2 size={11}/>Done! Next due: <strong>{nextRecurDate(task.recurrence,task.dueDate)}</strong></p>
                ):(
                  <div className="flex gap-2 flex-wrap mt-2">
                    <button onClick={async()=>{await onCompleteOccurrence&&onCompleteOccurrence(task.id);setRecurDone(true);}} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-lg" style={{background:"#4F46E5"}}>✓ Done for today</button>
                    <button onClick={async()=>{if(window.confirm("Mark this recurring task as permanently complete and stop recurring?"))await onEdit({...task,sta:"completed",recurrence:null});}} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-indigo-200 rounded-lg text-indigo-600 hover:bg-indigo-100">Complete &amp; Stop Recurring</button>
                  </div>
                )}
              </div>
            )}

            {task.sta==="review"&&canApprove&&(
              <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-3">
                <p className="text-sm font-semibold text-purple-800 mb-0.5 flex items-center gap-1.5"><ThumbsUp size={13}/>{dispName(task.asgn||task.own,allUsers)} marked this task complete</p>
                <p className="text-xs text-purple-600 mb-3">Review the work and approve to close it, or reopen if more work is needed.</p>
                <div className="flex gap-2">
                  <button onClick={()=>onReopen&&onReopen(task.id)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-slate-300 text-slate-600 bg-white text-sm font-medium hover:bg-slate-50"><CornerUpLeft size={13}/>Reopen</button>
                  <button onClick={()=>onApprove&&onApprove(task.id)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-white text-sm font-medium hover:opacity-90" style={{background:"#9333EA"}}><ThumbsUp size={13}/>Approve &amp; Close</button>
                </div>
              </div>
            )}

            {task.sta==="review"&&!canApprove&&task.asgn?.toLowerCase()===cu.email.toLowerCase()&&(
              <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-3">
                <p className="text-xs font-semibold text-purple-700 flex items-center gap-1.5"><ThumbsUp size={11}/>Marked complete — waiting for manager approval</p>
              </div>
            )}

            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <DetailRow icon={<Eye size={12}/>} label="Status"><StaBadge sta={locked?"blocked":task.sta}/></DetailRow>
              <DetailRow icon={<AlertCircle size={12}/>} label="Priority"><PriBadge pri={task.pri}/></DetailRow>
              <DetailRow icon={<Calendar size={12}/>} label="Due Date">
                <span className={od?"text-red-500 font-semibold":""}>{task.dueDate?fmtDate(task.dueDate):<span className="text-slate-400 italic">No due date</span>}</span>
                {od&&<span className="ml-2 text-xs text-red-500 font-semibold">Overdue</span>}
              </DetailRow>
              <DetailRow icon={<Lock size={12}/>} label="Visibility"><span className="flex items-center gap-1.5"><VisIcon type={task.vis}/>{vm.label}</span></DetailRow>
              <DetailRow icon={<User size={12}/>} label="Owner">
                <span>{dispName(task.own,allUsers)}</span>
                {isOwner&&<span className="ml-1.5 text-xs text-slate-400">(you)</span>}
              </DetailRow>
              {task.asgn&&task.asgn!==task.own&&(
                <DetailRow icon={<UserCheck size={12}/>} label="Assigned To">
                  <span>{dispName(task.asgn,allUsers)}</span>
                  {task.acc&&<span className="ml-2"><AccBadge acc={task.acc}/></span>}
                </DetailRow>
              )}
              {task.project&&(()=>{const p=projects.find(x=>x.spId===task.project);return p?<DetailRow icon={<Layers size={12}/>} label="Project"><span className="font-medium text-slate-700">{Graph.projectDisplayName(p)}</span></DetailRow>:null;})()}
              {task.src&&(
                <DetailRow icon={<Link2 size={12}/>} label="Source">
                  {task.src==="teams"?<TeamsBadge/>:<span className="text-slate-500">Manual</span>}
                </DetailRow>
              )}
            </div>

            {showStandup&&(
              <div className={"rounded-xl px-4 py-3 border flex items-center justify-between "+(task.standup?(stale?"bg-amber-50 border-amber-300":"bg-blue-50 border-blue-200"):"bg-slate-50 border-slate-200")}>
                <div>
                  <p className={"text-sm font-semibold flex items-center gap-1.5 "+(task.standup?(stale?"text-amber-800":"text-blue-700"):"text-slate-600")}>
                    <Radio size={13}/>Daily Standup
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {task.standup?(stale?"Needs update before next standup":"Included in Daily Standup"):"Not included in Daily Standup"}
                  </p>
                </div>
                <button onClick={()=>onStandupToggle&&onStandupToggle(task,!task.standup)} className={"relative inline-flex h-6 w-11 items-center rounded-full transition-colors "+(task.standup?"bg-blue-600":"bg-slate-200")}>
                  <span className={"inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform "+(task.standup?"translate-x-6":"translate-x-1")}/>
                </button>
              </div>
            )}

            {task.recurrence?.enabled&&(
              <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-center gap-2">
                <Repeat size={13} className="text-blue-500 shrink-0"/>
                <div>
                  <p className="text-sm font-semibold text-blue-700">Recurring</p>
                  <p className="text-xs text-blue-500">
                    {task.recurrence.freq==="daily"&&"Every day"}
                    {task.recurrence.freq==="weekdays"&&"Every weekday (Mon–Fri)"}
                    {task.recurrence.freq==="weekly"&&("Weekly"+(task.recurrence.days?.length>0?" on "+task.recurrence.days.join(", "):""))}
                    {task.recurrence.freq==="biweekly"&&("Every 2 weeks"+(task.recurrence.days?.length>0?" on "+task.recurrence.days.join(", "):""))}
                    {task.recurrence.freq==="monthly"&&`Monthly on day ${task.recurrence.dayOfMonth}`}
                    {task.recurrence.freq==="custom"&&`Every ${task.recurrence.interval} days`}
                  </p>
                </div>
              </div>
            )}

            {task.tags?.length>0&&(
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1"><Tag size={11}/>Tags</p>
                <div className="flex flex-wrap gap-1.5">
                  {task.tags.map(tag=><span key={tag} className="text-xs px-2.5 py-1 rounded-full border" style={{background:"#EEF4FF",color:BLUE,borderColor:"#BFCFEA"}}>{tag}</span>)}
                </div>
              </div>
            )}

            {deps.length>0&&(
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1"><GitBranch size={11}/>Must Complete First</p>
                {deps.map(dep=>(
                  <div key={dep.id} className={"flex items-center gap-3 px-3 py-2 rounded-lg border mb-1.5 "+(dep.sta==="completed"?"border-emerald-200 bg-emerald-50":"border-red-200 bg-red-50")}>
                    {dep.sta==="completed"?<CheckCircle2 size={14} className="text-emerald-500"/>:<Lock size={14} className="text-red-400"/>}
                    <p className={"flex-1 text-sm truncate "+(dep.sta==="completed"?"line-through text-slate-400":"font-medium")}>{dep.title}</p>
                    <StaBadge sta={dep.sta}/>
                  </div>
                ))}
              </div>
            )}

            <div className={"rounded-xl border px-4 py-3 "+(task.notes?"bg-amber-50 border-amber-100":"bg-slate-50 border-slate-200")}>
              <div className="flex items-center justify-between mb-1.5">
                <p className={"text-xs font-bold uppercase tracking-widest flex items-center gap-1 "+(task.notes?"text-amber-600":"text-slate-400")}><MessageSquare size={11}/>Notes</p>
                {canEdit&&inlineNote===null&&<button onClick={()=>setInlineNote(task.notes||"")} className="text-xs text-slate-400 hover:text-blue-600 flex items-center gap-1"><Edit2 size={10}/>{task.notes?"Edit":"Add"}</button>}
              </div>
              {inlineNote!==null?(
                <div>
                  <textarea autoFocus value={inlineNote} onChange={e=>setInlineNote(e.target.value)} rows={3} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none resize-none bg-white" placeholder="Add notes..."/>
                  <div className="flex gap-2 mt-1.5">
                    <button onClick={saveInlineNote} disabled={noteSaving} className="text-xs px-3 py-1 text-white rounded-lg font-semibold flex items-center gap-1 disabled:opacity-50" style={{background:"#1D5FA5"}}>{noteSaving&&<span className="animate-spin inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full"/>}<Save size={10}/>Save</button>
                    <button onClick={()=>setInlineNote(null)} className="text-xs px-3 py-1 border border-slate-200 rounded-lg text-slate-500 hover:bg-slate-100">Cancel</button>
                  </div>
                </div>
              ):(
                task.notes
                  ?<div>
                    <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{task.notes}</p>
                    {task.noteHistory&&task.noteHistory.length>0&&(()=>{const last=task.noteHistory[task.noteHistory.length-1];const d=new Date(last.date);const ts=d.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})+", "+d.toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"});const who=dispName(last.by,allUsers);return<p className="text-xs text-slate-400 italic mt-2">Last updated by {who} — {ts}</p>;})()}
                  </div>
                  :<p className="text-sm text-slate-400 italic">{canEdit?"Click Add to write a note...":"No notes."}</p>
              )}
            </div>

            {task.standup&&sortedHistory.length>0&&(
              <div>
                <button onClick={()=>setShowHistory(h=>!h)} className="w-full flex items-center justify-between text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 hover:text-slate-600">
                  <span className="flex items-center gap-1"><Radio size={11}/>Standup History ({sortedHistory.length})</span>
                  <span>{showHistory?"▲":"▼"}</span>
                </button>
                {showHistory&&(
                  <div className="space-y-2">
                    {sortedHistory.map((h,i)=>(
                      <div key={i} className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-semibold text-slate-600">{fmtDate(h.date)}</span>
                          <StaBadge sta={h.status}/>
                        </div>
                        {h.note&&<p className="text-xs text-slate-500 leading-relaxed">{h.note}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Attachments section — always visible when canEdit or has docs */}
            {(canEdit||task.docs?.length>0)&&(
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1"><Paperclip size={11}/>Attachments <span className="font-normal text-slate-300 normal-case tracking-normal">({(task.docs||[]).length}/3)</span></p>
                </div>
                {/* Existing docs list */}
                {(task.docs||[]).map((doc,i)=>{
                  const meta=DOC_META[doc.t]||DOC_META.sharepoint;
                  const icons={sharepoint:FileText,onedrive:FileText,teams:MessageSquare,outlook:Mail,upload:Paperclip};
                  const DI=icons[doc.t]||FileText;
                  return(
                    <div key={i} className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-3 py-2 mb-1.5 group">
                      <span className={"p-1.5 rounded-lg "+(doc.t==="upload"?"bg-indigo-50 text-indigo-600":meta.cls)}><DI size={13}/></span>
                      <div className="flex-1 min-w-0">
                        <a href={doc.u} target="_blank" rel="noreferrer" className="text-sm font-medium truncate block hover:underline text-slate-700">{doc.n}</a>
                        <p className="text-xs text-slate-400">{doc.t==="upload"?"Uploaded file":meta.label}</p>
                      </div>
                      <a href={doc.u} target="_blank" rel="noreferrer" className="text-slate-300 hover:text-blue-500"><ExternalLink size={11}/></a>
                      {canEdit&&<button onClick={()=>removeAttachment(i)} className="text-slate-200 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity ml-1"><X size={13}/></button>}
                    </div>
                  );
                })}
                {/* Drop zone — only show if under limit and canEdit */}
                {canEdit&&(task.docs||[]).length<3&&(
                  <div
                    onDragOver={e=>{e.preventDefault();setDragOver(true);}}
                    onDragLeave={()=>setDragOver(false)}
                    onDrop={e=>{e.preventDefault();setDragOver(false);const f=e.dataTransfer.files[0];if(f)uploadAttachment(f);}}
                    className={"border-2 border-dashed rounded-xl px-4 py-4 text-center transition-colors "+(dragOver?"border-blue-400 bg-blue-50":"border-slate-200 hover:border-slate-300 hover:bg-slate-50")}
                  >
                    {attachUploading?(
                      <p className="text-xs text-slate-400 flex items-center justify-center gap-1.5"><span className="animate-spin inline-block w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full"/>Uploading…</p>
                    ):(
                      <div className="space-y-2">
                        <Upload size={14} className="mx-auto text-slate-300"/>
                        <div className="flex items-center justify-center gap-2 flex-wrap">
                          <label className="cursor-pointer text-xs text-blue-500 hover:underline flex items-center gap-1">
                            <input type="file" className="hidden" onChange={e=>e.target.files[0]&&uploadAttachment(e.target.files[0])}/>
                            Browse
                          </label>
                          <span className="text-slate-300 text-xs">·</span>
                          <span className="text-xs text-slate-400">Drop file here</span>
                          <span className="text-slate-300 text-xs">·</span>
                          <button type="button" onClick={pasteFromClipboard} className="text-xs text-blue-500 hover:underline flex items-center gap-1"><Paperclip size={10}/>Paste from Clipboard</button>
                        </div>
                        <p className="text-xs text-slate-300">Up to 3 files · 25 MB each</p>
                      </div>
                    )}
                  </div>
                )}
                {attachError&&<p className="text-xs text-red-500 mt-1">{attachError}</p>}
              </div>
            )}
            {/* Activity log */}
            {task.spId&&(
              <div>
                <button onClick={()=>setShowActivity(a=>!a)} className="w-full flex items-center justify-between text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 hover:text-slate-600">
                  <span className="flex items-center gap-1"><History size={11}/>Activity</span>
                  {showActivity?<ChevronUp size={12}/>:<ChevronDown size={12}/>}
                </button>
                {showActivity&&(
                  <div className="space-y-1">
                    {actLoading&&<p className="text-xs text-slate-400 text-center py-3">Loading…</p>}
                    {!actLoading&&activity.length===0&&<p className="text-xs text-slate-400 italic">No activity recorded yet.</p>}
                    {!actLoading&&activity.map((a,i)=>{
                      const d=a.changedAt?new Date(a.changedAt):"";
                      const dateStr=d?d.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})+" "+d.toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"}):"";
                      const ACTION_COLOR={created:"text-emerald-600",deleted:"text-red-500",accepted:"text-emerald-600",declined:"text-red-500",status_changed:"text-blue-600",assigned:"text-indigo-600",standup_note:"text-amber-600"};
                      return(
                        <div key={i} className="flex items-start gap-2.5 px-3 py-2 rounded-lg bg-slate-50 border border-slate-100">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-slate-700 leading-snug">{a.details||a.action.replace(/_/g," ")}</p>
                            <p className="text-xs text-slate-400 mt-0.5">{a.changedBy.split("@")[0]} · {dateStr}</p>
                          </div>
                          <span className={"text-xs font-semibold shrink-0 "+(ACTION_COLOR[a.action]||"text-slate-500")}>{a.action.replace(/_/g," ")}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
    {showDepsPicker&&<DepsPicker all={all} cu={cu} excludeId={task.id} selected={editForm.deps||[]} onChange={deps=>efUpd("deps",deps)} onClose={()=>setShowDepsPicker(false)} projects={projects}/>}
    {showAssigneePicker&&<AssigneePicker users={assignable} value={editForm.asgn||""} onChange={v=>efUpd("asgn",v)} onClose={()=>setShowAssigneePicker(false)} required/>}
    {showReassignPicker&&<AssigneePicker users={assignable} value={task.asgn||""} onChange={v=>{if(onEdit)onEdit({...task,asgn:v,sta:"pending",acc:"pending",reqBy:cu.email});setShowReassignPicker(false);}} onClose={()=>setShowReassignPicker(false)} required/>}
    {showUpdateModal&&(
      <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/30 rounded-xl">
        <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-80 p-5 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <p className="font-semibold text-slate-800 text-sm flex items-center gap-1.5"><MessageSquare size={14} className="text-amber-500"/>Request Update</p>
            <button onClick={()=>setShowUpdateModal(false)} className="text-slate-400 hover:text-slate-600"><X size={15}/></button>
          </div>
          <p className="text-xs text-slate-500">Sending to <span className="font-medium text-slate-700">{dispName(task.asgn,allUsers)||task.asgn.split("@")[0]}</span></p>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Note <span className="font-normal text-slate-400">(optional)</span></label>
            <textarea value={updateNote} onChange={e=>setUpdateNote(e.target.value)} rows={3} placeholder="What do you need an update on?" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400 resize-none"/>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={()=>setShowUpdateModal(false)} className="text-xs px-3 py-1.5 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">Cancel</button>
            <button disabled={updateStatus==="sending"} onClick={async()=>{setShowUpdateModal(false);setUpdateStatus("sending");try{await onRequestUpdate(task,updateNote.trim());setUpdateStatus("sent");setTimeout(()=>setUpdateStatus(null),3000);}catch(e){setUpdateStatus("error");setTimeout(()=>setUpdateStatus(null),4000);}}} className="text-xs px-3 py-1.5 rounded-lg text-white font-semibold flex items-center gap-1 disabled:opacity-60" style={{background:"#E89A0A"}}>{updateStatus==="sending"?<span className="animate-spin inline-block w-2.5 h-2.5 border-2 border-white border-t-transparent rounded-full"/>:<MessageSquare size={11}/>}Send Request</button>
          </div>
        </div>
      </div>
    )}
</>
  );
}
